import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axiosInstance from '../api/Axios';
import HomeNavBar from "../components/HomeNavBar";
import backgroundImg from '../assets/images/background.jpg';

// Content Section Component
const ContentSection = ({ userInfo }) => {
  return (
    <div style={{
      borderRadius: '10px',
      padding: '2rem',
      margin: '1rem 0'
    }}>
      <h1 style={{
        fontSize: '3.5rem',
        fontWeight: 'bold',
        marginBottom: '1rem',
        color: '#fff'
      }}>Reset Your Password</h1>
      <p style={{
        fontSize: '1.2rem',
        lineHeight: '1.6',
        color: '#fff'
      }}>
        You're about to set a new password for your account. Choose a strong, unique password to keep your account secure.
      </p>
      {userInfo && (
        <div style={{
          backgroundColor: 'rgba(255, 255, 255, 0.1)',
          padding: '1.5rem',
          borderRadius: '8px',
          marginTop: '1.5rem'
        }}>
          <h3 style={{ color: '#fff', marginBottom: '1rem' }}>Account Details</h3>
          <p style={{ color: '#fff', margin: '0.5rem 0' }}>
            <strong>Email:</strong> {userInfo.user_email}
          </p>
          <p style={{ color: '#fff', margin: '0.5rem 0' }}>
            <strong>Role:</strong> {userInfo.role}
          </p>
        </div>
      )}
      <p style={{
        fontSize: '1.2rem',
        lineHeight: '1.6',
        color: '#fff',
        marginTop: '1.5rem'
      }}>
        Your password should be at least 8 characters long and include a mix of uppercase and lowercase letters, 
        numbers, and special characters for maximum security.
      </p>
    </div>
  );
};

// Password Reset Form Component
const ResetPasswordForm = ({ token, onSuccess }) => {
  const [formData, setFormData] = useState({
    password: '',
    confirm_password: ''
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear errors when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validatePassword = (password) => {
    const minLength = 8;
    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumbers = /\d/.test(password);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);

    if (password.length < minLength) {
      return 'Password must be at least 8 characters long';
    }
    if (!hasUpperCase) {
      return 'Password must contain at least one uppercase letter';
    }
    if (!hasLowerCase) {
      return 'Password must contain at least one lowercase letter';
    }
    if (!hasNumbers) {
      return 'Password must contain at least one number';
    }
    if (!hasSpecialChar) {
      return 'Password must contain at least one special character';
    }
    return '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({});
    setMessage('');

    // Validate passwords
    const passwordError = validatePassword(formData.password);
    if (passwordError) {
      setErrors({ password: passwordError });
      return;
    }

    if (formData.password !== formData.confirm_password) {
      setErrors({ confirm_password: 'Passwords do not match' });
      return;
    }

    setLoading(true);

    try {
      await axiosInstance.post(`/api/accounts/reset-password/${token}/`, {
        password: formData.password,
        confirm_password: formData.confirm_password
      });

      setMessage('Password reset successfully!');
      onSuccess();
      
    } catch (error) {
      console.error('Password reset request failed:', error);
      
      if (error.response) {
        const { status, data } = error.response;
        console.error('Reset failed with status:', status, data);
        
        if (data.password) {
          setErrors({ password: Array.isArray(data.password) ? data.password.join(', ') : data.password });
        } else if (data.confirm_password) {
          setErrors({ confirm_password: Array.isArray(data.confirm_password) ? data.confirm_password.join(', ') : data.confirm_password });
        } else {
          setMessage(data.error || data.message || `Error ${status}: Please try again.`);
        }
      } else if (error.request) {
        console.error('No response received:', error.request);
        setMessage('Network error. Please check your connection and try again.');
      } else {
        console.error('Request setup error:', error.message);
        setMessage('An error occurred. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      backgroundColor: 'rgba(255, 255, 255, 0.95)',
      borderRadius: '10px',
      padding: '2rem',
      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
      margin: '1rem 0'
    }}>
      <h2 style={{
        fontSize: '2rem',
        fontWeight: 'bold',
        marginBottom: '1.5rem',
        color: '#333',
        textAlign: 'center'
      }}>Set New Password</h2>

      {message && (
        <div style={{
          padding: '1rem',
          borderRadius: '5px',
          marginBottom: '1rem',
          backgroundColor: message.includes('successfully') ? '#d4edda' : '#f8d7da',
          color: message.includes('successfully') ? '#155724' : '#721c24',
          border: `1px solid ${message.includes('successfully') ? '#c3e6cb' : '#f5c6cb'}`
        }}>
          {message}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div style={{ marginBottom: '1.5rem' }}>
          <label style={{
            display: 'block',
            marginBottom: '0.5rem',
            fontWeight: 'bold',
            color: '#333'
          }}>
            New Password *
          </label>
          <input
            type="password"
            name="password"
            value={formData.password}
            onChange={handleChange}
            required
            style={{
              width: '100%',
              padding: '0.75rem',
              border: errors.password ? '2px solid #dc3545' : '1px solid #ddd',
              borderRadius: '5px',
              fontSize: '1rem',
              backgroundColor: '#fff'
            }}
            placeholder="Enter your new password"
          />
          {errors.password && (
            <div style={{ color: '#dc3545', fontSize: '0.875rem', marginTop: '0.25rem' }}>
              {errors.password}
            </div>
          )}
          <div style={{ fontSize: '0.875rem', color: '#6c757d', marginTop: '0.25rem' }}>
            Password must be at least 8 characters with uppercase, lowercase, numbers, and special characters
          </div>
        </div>

        <div style={{ marginBottom: '1.5rem' }}>
          <label style={{
            display: 'block',
            marginBottom: '0.5rem',
            fontWeight: 'bold',
            color: '#333'
          }}>
            Confirm Password *
          </label>
          <input
            type="password"
            name="confirm_password"
            value={formData.confirm_password}
            onChange={handleChange}
            required
            style={{
              width: '100%',
              padding: '0.75rem',
              border: errors.confirm_password ? '2px solid #dc3545' : '1px solid #ddd',
              borderRadius: '5px',
              fontSize: '1rem',
              backgroundColor: '#fff'
            }}
            placeholder="Confirm your new password"
          />
          {errors.confirm_password && (
            <div style={{ color: '#dc3545', fontSize: '0.875rem', marginTop: '0.25rem' }}>
              {errors.confirm_password}
            </div>
          )}
        </div>

        <button
          type="submit"
          disabled={loading}
          style={{
            width: '100%',
            padding: '0.75rem',
            backgroundColor: loading ? '#6c757d' : '#007bff',
            color: '#fff',
            border: 'none',
            borderRadius: '5px',
            fontSize: '1rem',
            fontWeight: 'bold',
            cursor: loading ? 'not-allowed' : 'pointer',
            transition: 'background-color 0.3s'
          }}
        >
          {loading ? 'Resetting Password...' : 'Reset Password'}
        </button>
      </form>
    </div>
  );
};

// Success Message Component
const SuccessMessage = () => {
  return (
    <div style={{
      backgroundColor: 'rgba(255, 255, 255, 0.95)',
      borderRadius: '10px',
      padding: '2rem',
      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
      margin: '1rem 0',
      textAlign: 'center'
    }}>
      <div style={{
        fontSize: '3rem',
        color: '#28a745',
        marginBottom: '1rem'
      }}>
        ✓
      </div>
      <h2 style={{
        fontSize: '2rem',
        fontWeight: 'bold',
        marginBottom: '1rem',
        color: '#333'
      }}>Password Reset Successfully!</h2>
      <p style={{
        fontSize: '1.1rem',
        color: '#666',
        marginBottom: '2rem'
      }}>
        Your password has been reset successfully. You can now log in with your new password.
      </p>
      <Link
        to="/Login"
        style={{
          display: 'inline-block',
          padding: '0.75rem 2rem',
          backgroundColor: '#007bff',
          color: '#fff',
          textDecoration: 'none',
          borderRadius: '5px',
          fontSize: '1rem',
          fontWeight: 'bold',
          transition: 'background-color 0.3s'
        }}
        onMouseOver={(e) => e.target.style.backgroundColor = '#0056b3'}
        onMouseOut={(e) => e.target.style.backgroundColor = '#007bff'}
      >
        Go to Login Page
      </Link>
    </div>
  );
};

// Main Reset Password Page Component
const ResetPasswordPage = () => {
  const { token } = useParams();
  const [userInfo, setUserInfo] = useState(null);
  const [isValidToken, setIsValidToken] = useState(null);
  const [isReset, setIsReset] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Validate token and get user info
    const validateToken = async () => {
      try {
        const response = await axiosInstance.get(`/api/accounts/reset-password/${token}/`);
        const data = response.data;
        
        if (data.valid) {
          setIsValidToken(true);
          setUserInfo(data);
        } else {
          console.error('Token validation returned invalid:', data);
          setIsValidToken(false);
        }
      } catch (error) {
        console.error('Token validation error:', error);
        setIsValidToken(false);
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      validateToken();
    } else {
      setIsValidToken(false);
      setLoading(false);
    }
  }, [token]);

  const handleSuccess = () => {
    setIsReset(true);
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        fontSize: '1.2rem'
      }}>
        Loading...
      </div>
    );
  }

  return (
    <div>
      {/* Bootstrap 5 CSS Link */}
      <link 
        href="https://cdnjs.cloudflare.com/ajax/libs/bootstrap/5.3.0/css/bootstrap.min.css" 
        rel="stylesheet" 
      />
      
      <div style={{ fontFamily: 'Arial, sans-serif' }}>
        {/* Navigation */}
        <HomeNavBar />

        {/* Hero Section */}
        <section style={{
          backgroundImage: `linear-gradient(rgba(0,0,0,0.4), rgba(0,0,0,0.4)), url(${backgroundImg})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed',
          minHeight: 'calc(100vh - 80px)',
          display: 'flex',
          alignItems: 'center'
        }}>
          <div className="container">
            <div className="row">
              {/* Form Section (1/3) - Left side */}
              <div className="col-lg-4">
                {isValidToken === false ? (
                  <div style={{
                    backgroundColor: 'rgba(255, 255, 255, 0.95)',
                    borderRadius: '10px',
                    padding: '2rem',
                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                    textAlign: 'center'
                  }}>
                    <h2 style={{ color: '#dc3545', marginBottom: '1rem' }}>Invalid or Expired Link</h2>
                    <p style={{ color: '#666', marginBottom: '2rem' }}>
                      The password reset link is invalid or has expired. Please request a new password reset link.
                    </p>
                    <Link
                      to="/forgot-password"
                      style={{
                        display: 'inline-block',
                        padding: '0.75rem 2rem',
                        backgroundColor: '#007bff',
                        color: '#fff',
                        textDecoration: 'none',
                        borderRadius: '5px',
                        fontSize: '1rem',
                        fontWeight: 'bold',
                        marginRight: '1rem'
                      }}
                    >
                      Request New Link
                    </Link>
                    <Link
                      to="/Login"
                      style={{
                        display: 'inline-block',
                        padding: '0.75rem 2rem',
                        backgroundColor: '#6c757d',
                        color: '#fff',
                        textDecoration: 'none',
                        borderRadius: '5px',
                        fontSize: '1rem',
                        fontWeight: 'bold'
                      }}
                    >
                      Go to Login
                    </Link>
                  </div>
                ) : isReset ? (
                  <SuccessMessage />
                ) : (
                  <ResetPasswordForm token={token} onSuccess={handleSuccess} />
                )}
              </div>

              {/* Content Section (2/3) - Right side */}
              <div className="col-lg-8">
                <ContentSection userInfo={userInfo} />
              </div>
            </div>
          </div>
        </section>
      </div>

      {/* Bootstrap 5 JS Script */}
      <script 
        src="https://cdnjs.cloudflare.com/ajax/libs/bootstrap/5.3.0/js/bootstrap.bundle.min.js"
      ></script>
    </div>
  );
};

export default ResetPasswordPage;
